import { useEffect, useRef, useState } from "react";
import {
  CHANNELS,
  PRIORITY_COLOR,
  PRIORITY_FILTER_OPTIONS,
  PRIORITY_LABEL,
  STATUS_FILTER_OPTIONS,
  STATUS_LABEL,
} from "../constants.ts";

function FilterChip({
  active,
  onClick,
  children,
  activeColor,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  activeColor?: string;
  title?: string;
}) {
  const border = active ? (activeColor || "var(--primary)") : "var(--border)";
  const bg = active ? (activeColor || "var(--primary)") : "transparent";
  const color = active ? "#fff" : "var(--text)";
  return (
    <button
      type="button"
      className="support-filter-chip"
      onClick={onClick}
      title={title}
      style={{ borderColor: border, background: bg, color }}
    >
      {children}
    </button>
  );
}

export function FilterBar({
  channelFilter,
  statusFilter,
  priorityFilter,
  followupOnly,
  myAssignedOnly = false,
  hasOpenTasksOnly = false,
  unassignedOnly = false,
  embedded = false,
  onToggleChannel,
  onToggleStatus,
  onTogglePriority,
  onFollowupOnlyChange,
  onMyAssignedOnlyChange,
  onHasOpenTasksOnlyChange,
  onUnassignedOnlyChange,
  onClearChannels,
  onClearStatuses,
  onClearPriorities,
  onClearAll,
  showResetToDefault = false,
  filtersAtDefault = false,
}: {
  channelFilter: string[];
  statusFilter: string[];
  priorityFilter: string[];
  followupOnly: boolean;
  myAssignedOnly?: boolean;
  hasOpenTasksOnly?: boolean;
  /** 未割当のみ（改善 E-4） */
  unassignedOnly?: boolean;
  embedded?: boolean;
  onToggleChannel: (key: string) => void;
  onToggleStatus: (status: string) => void;
  onTogglePriority: (priority: string) => void;
  onFollowupOnlyChange: (v: boolean) => void;
  onMyAssignedOnlyChange?: (v: boolean) => void;
  onHasOpenTasksOnlyChange?: (v: boolean) => void;
  onUnassignedOnlyChange?: (v: boolean) => void;
  onClearChannels: () => void;
  onClearStatuses: () => void;
  onClearPriorities: () => void;
  onClearAll: () => void;
  showResetToDefault?: boolean;
  filtersAtDefault?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (evt: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(evt.target as Node)) {
        setOpen(false);
      }
    };
    // 開くクリックと同じイベントで即閉じないよう、リスナー登録を次 tick にずらす
    const id = window.setTimeout(() => document.addEventListener("click", handler), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("click", handler);
    };
  }, [open]);

  const toggleOpen = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setOpen((v) => !v);
  };

  const badges: { key: string; label: string; onRemove: () => void; color?: string }[] = [];
  for (const key of channelFilter) {
    const ch = CHANNELS.find((c) => c.key === key);
    badges.push({ key: `ch-${key}`, label: ch?.label || key, onRemove: () => onToggleChannel(key) });
  }
  if (followupOnly) {
    badges.push({ key: "followup", label: "後日対応のみ", onRemove: () => onFollowupOnlyChange(false) });
  }
  if (myAssignedOnly) {
    badges.push({ key: "mine", label: "自分担当", onRemove: () => onMyAssignedOnlyChange?.(false) });
  }
  if (hasOpenTasksOnly) {
    badges.push({ key: "tasks", label: "タスクあり", onRemove: () => onHasOpenTasksOnlyChange?.(false) });
  }
  if (unassignedOnly) {
    badges.push({ key: "unassigned", label: "未割当", onRemove: () => onUnassignedOnlyChange?.(false) });
  }
  for (const s of statusFilter) {
    badges.push({ key: `st-${s}`, label: STATUS_LABEL[s] || s, onRemove: () => onToggleStatus(s) });
  }
  for (const p of priorityFilter) {
    badges.push({
      key: `pr-${p}`,
      label: PRIORITY_LABEL[p] || p,
      onRemove: () => onTogglePriority(p),
      color: PRIORITY_COLOR[p],
    });
  }

  const panel = (
    <>
      <div className="support-filter-bar__groups">
        <div className="support-filter-bar__group">
          <span className="support-filter-bar__label">チャネル</span>
          {CHANNELS.map((c) => (
            <FilterChip
              key={c.key}
              active={channelFilter.includes(c.key)}
              onClick={() => onToggleChannel(c.key)}
              title={c.hint}
            >
              {c.label}
            </FilterChip>
          ))}
          {channelFilter.length > 0 && (
            <button type="button" className="support-filter-bar__link" onClick={onClearChannels}>クリア</button>
          )}
        </div>

        <div className="support-filter-bar__divider" aria-hidden />

        <div className="support-filter-bar__group">
          <span className="support-filter-bar__label">後日</span>
          <FilterChip active={followupOnly} onClick={() => onFollowupOnlyChange(!followupOnly)} title="後日対応（再訪）のチケットのみ">
            のみ表示
          </FilterChip>
        </div>

        <div className="support-filter-bar__divider" aria-hidden />

        <div className="support-filter-bar__group">
          <span className="support-filter-bar__label">担当</span>
          <FilterChip
            active={myAssignedOnly}
            onClick={() => onMyAssignedOnlyChange?.(!myAssignedOnly)}
            title="自分が担当のチケットのみ"
          >
            自分担当
          </FilterChip>
          <FilterChip
            active={hasOpenTasksOnly}
            onClick={() => onHasOpenTasksOnlyChange?.(!hasOpenTasksOnly)}
            title="未完了タスクが1件以上あるチケット"
          >
            タスクあり
          </FilterChip>
          <FilterChip
            active={unassignedOnly}
            onClick={() => onUnassignedOnlyChange?.(!unassignedOnly)}
            title="担当が未設定のチケットのみ"
          >
            未割当
          </FilterChip>
        </div>

        <div className="support-filter-bar__divider" aria-hidden />

        <div className="support-filter-bar__group">
          <span className="support-filter-bar__label">状態</span>
          {STATUS_FILTER_OPTIONS.map((s) => (
            <FilterChip key={s} active={statusFilter.includes(s)} onClick={() => onToggleStatus(s)}>
              {STATUS_LABEL[s] || s}
            </FilterChip>
          ))}
          {statusFilter.length > 0 && (
            <button type="button" className="support-filter-bar__link" onClick={onClearStatuses}>クリア</button>
          )}
        </div>

        <div className="support-filter-bar__divider" aria-hidden />

        <div className="support-filter-bar__group">
          <span className="support-filter-bar__label">優先</span>
          {PRIORITY_FILTER_OPTIONS.map((p) => (
            <FilterChip
              key={p}
              active={priorityFilter.includes(p)}
              onClick={() => onTogglePriority(p)}
              activeColor={PRIORITY_COLOR[p]}
            >
              {PRIORITY_LABEL[p] || p}
            </FilterChip>
          ))}
          {priorityFilter.length > 0 && (
            <button type="button" className="support-filter-bar__link" onClick={onClearPriorities}>クリア</button>
          )}
        </div>
      </div>

      <div className="support-filter-bar__expanded-actions">
        {showResetToDefault && (
          <button type="button" className="support-filter-bar__link" onClick={onClearAll}>
            既定に戻す
          </button>
        )}
        <button type="button" className="support-filter-bar__toggle" onClick={toggleOpen}>
          ▴ 閉じる
        </button>
      </div>
    </>
  );

  return (
    <div ref={rootRef} className={"support-filter-bar" + (open ? " support-filter-bar--open" : "") + (embedded ? " support-filter-bar--embedded" : "")}>
      <div className="support-filter-bar__collapsed">
        <button
          type="button"
          className={"support-filter-bar__toggle support-filter-bar__toggle--lead" + (open ? " support-filter-bar__toggle--active" : "")}
          onClick={toggleOpen}
          aria-expanded={open}
        >
          フィルタ {open ? "▴" : "▾"}
        </button>
        <div className="support-filter-bar__badges">
          {filtersAtDefault ? (
            <span className="support-filter-bar__hint" title="入電・手動対応の未完了チケット（機体監視・解決済みは非表示）">
              入電対応（未完了）
            </span>
          ) : badges.length === 0 ? (
            <span className="support-filter-bar__hint">全件</span>
          ) : (
            badges.map((b) => (
              <span
                key={b.key}
                className="support-filter-bar__badge"
                style={b.color ? { borderColor: b.color, color: b.color } : undefined}
              >
                {b.label}
                <button type="button" className="support-filter-bar__badge-remove" onClick={b.onRemove} aria-label={`${b.label} を外す`}>
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        {showResetToDefault && (
          <button type="button" className="support-filter-bar__link support-filter-bar__link--end" onClick={onClearAll}>
            既定に戻す
          </button>
        )}
      </div>

      {open && (
        embedded ? (
          <div className="support-filter-bar__popover" onClick={(e) => e.stopPropagation()}>
            {panel}
          </div>
        ) : (
          <div className="support-filter-bar__expanded">
            {panel}
          </div>
        )
      )}
    </div>
  );
}
